import React, { useState, useEffect } from 'react';
import { XMarkIcon, UserCircleIcon, CalendarDaysIcon, ClockIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { attendanceAPI } from '../services/api';
import moment from 'moment';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

function UserProfileModal({ isOpen, onClose, user }) {
  const [userStats, setUserStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('week'); // 'today', 'week', 'month', 'custom'
  const [customStartDate, setCustomStartDate] = useState(moment().subtract(7, 'days').toDate());
  const [customEndDate, setCustomEndDate] = useState(new Date());

  const loadUserStats = async () => {
    try {
      setLoading(true);
      setError(null);

      let startDate, endDate;
      
      switch (selectedPeriod) {
        case 'today':
          startDate = moment().startOf('day').format('YYYY-MM-DD');
          endDate = moment().endOf('day').format('YYYY-MM-DD');
          break;
        case 'week':
          startDate = moment().startOf('week').format('YYYY-MM-DD');
          endDate = moment().endOf('week').format('YYYY-MM-DD');
          break;
        case 'month':
          startDate = moment().startOf('month').format('YYYY-MM-DD');
          endDate = moment().endOf('month').format('YYYY-MM-DD');
          break;
        case 'custom':
          startDate = moment(customStartDate).format('YYYY-MM-DD');
          endDate = moment(customEndDate).format('YYYY-MM-DD');
          break;
        default:
          startDate = moment().startOf('week').format('YYYY-MM-DD');
          endDate = moment().endOf('week').format('YYYY-MM-DD');
      }

      const userIdentifier = user?.id || user?.uidNumber;
      const response = await attendanceAPI.getAttendanceSummary(userIdentifier, {
        startDate,
        endDate
      });

      setUserStats(response.data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && user) {
      loadUserStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user, selectedPeriod, customStartDate, customEndDate]);

  const getCompletionStatus = (hoursWorked, requiredHours) => {
    if (!requiredHours) return { status: 'unknown', percentage: 0 };
    
    const percentage = (hoursWorked / requiredHours) * 100;
    
    if (percentage >= 100) {
      return { status: 'complete', percentage: Math.min(percentage, 100) };
    } else if (percentage >= 80) {
      return { status: 'warning', percentage };
    } else {
      return { status: 'incomplete', percentage };
    }
  };

  const formatPeriodLabel = () => {
    switch (selectedPeriod) {
      case 'today':
        return 'Today';
      case 'week':
        return 'This Week';
      case 'month':
        return 'This Month';
      case 'custom':
        return `${moment(customStartDate).format('MMM DD, YYYY')} - ${moment(customEndDate).format('MMM DD, YYYY')}`;
      default:
        return 'This Week';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'complete':
        return 'text-green-600 bg-green-100';
      case 'warning':
        return 'text-yellow-600 bg-yellow-100';
      case 'incomplete':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'complete':
        return <CheckCircleIcon className="h-5 w-5" />;
      case 'warning':
      case 'incomplete':
        return <ExclamationTriangleIcon className="h-5 w-5" />;
      default:
        return <ClockIcon className="h-5 w-5" />;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="flex-shrink-0 h-12 w-12">
                  <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center overflow-hidden">
                    {user?.profilePhotoUrl ? (
                      <img
                        src={user.profilePhotoUrl}
                        alt={`${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'User'}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <UserCircleIcon className="h-8 w-8 text-primary-600" />
                    )}
                  </div>
                </div>
                <div className="ml-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    {user?.firstName} {user?.lastName}
                  </h3>
                  <p className="text-sm text-gray-500">{user?.officialEmail}</p>
                  <div className="flex items-center mt-1">
                    <span className="text-xs text-gray-400">UID: {user?.uidNumber}</span>
                    {user?.Department && (
                      <>
                        <span className="mx-2 text-gray-300">•</span>
                        <span className="text-xs text-gray-400">{user?.Department}</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Period Selector */}
            <div className="mb-6">
              <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
                {[
                  { key: 'today', label: 'Today' },
                  { key: 'week', label: 'This Week' },
                  { key: 'month', label: 'This Month' },
                  { key: 'custom', label: 'Custom' }
                ].map((period) => (
                  <button
                    key={period.key}
                    onClick={() => setSelectedPeriod(period.key)}
                    className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                      selectedPeriod === period.key
                        ? 'bg-white text-primary-600 shadow-sm'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {period.label}
                  </button>
                ))}
              </div>
              
              {/* Custom Date Range Picker */}
              {selectedPeriod === 'custom' && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Select Date Range</label>
                  <div className="flex items-center space-x-3">
                    <div className="flex-1">
                      <DatePicker
                        selected={customStartDate}
                        onChange={(date) => setCustomStartDate(date)}
                        selectsStart
                        startDate={customStartDate}
                        endDate={customEndDate}
                        maxDate={customEndDate}
                        className="w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm px-3 py-2"
                        placeholderText="Start date"
                        dateFormat="MMM dd, yyyy"
                      />
                    </div>
                    <span className="text-gray-500">to</span>
                    <div className="flex-1">
                      <DatePicker
                        selected={customEndDate}
                        onChange={(date) => setCustomEndDate(date)}
                        selectsEnd
                        startDate={customStartDate}
                        endDate={customEndDate}
                        minDate={customStartDate}
                        maxDate={new Date()}
                        className="w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm px-3 py-2"
                        placeholderText="End date"
                        dateFormat="MMM dd, yyyy"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Content */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="spinner"></div>
                <span className="ml-2 text-gray-600">Loading stats...</span>
              </div>
            ) : error ? (
              <div className="text-center py-12">
                <div className="text-red-600 mb-2">Error: {error}</div>
                <button
                  onClick={loadUserStats}
                  className="bg-primary-600 text-white px-4 py-2 rounded-md text-sm hover:bg-primary-700"
                >
                  Retry
                </button>
              </div>
            ) : userStats ? (
              <div className="space-y-6">
                {/* Summary Stats */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <CalendarDaysIcon className="h-6 w-6 text-blue-600" />
                      <div className="ml-3">
                        <p className="text-sm font-medium text-blue-900">Working Days</p>
                        <p className="text-lg font-semibold text-blue-600">
                          {userStats.summary.workingDays} / {userStats.summary.totalDays}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-green-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <ClockIcon className="h-6 w-6 text-green-600" />
                      <div className="ml-3">
                        <p className="text-sm font-medium text-green-900">Total Hours</p>
                        <p className="text-lg font-semibold text-green-600">
                          {userStats.summary.totalHours}h
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-purple-50 rounded-lg p-4">
                    <div className="flex items-center">
                      <ClockIcon className="h-6 w-6 text-purple-600" />
                      <div className="ml-3">
                        <p className="text-sm font-medium text-purple-900">Avg/Day</p>
                        <p className="text-lg font-semibold text-purple-600">
                          {userStats.summary.avgHoursPerDay}h
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Working Hours Analysis */}
                {user?.workingPolicy && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">
                      {formatPeriodLabel()} Performance
                    </h4>
                    
                    {(() => {
                      let requiredHours;
                      const policyDaysForCompletion = Number(
                        userStats.summary.elapsedDays ?? userStats.summary.totalDays
                      ) || 0;
                      const leaveHoursDeducted = Number(userStats.summary.leaveHoursDeducted) || 0;
                      
                      if (selectedPeriod === 'today') {
                        requiredHours = user.workingPolicy.requiredDailyHours * policyDaysForCompletion;
                      } else if (selectedPeriod === 'week' || selectedPeriod === 'month' || selectedPeriod === 'custom') {
                        requiredHours = user.workingPolicy.requiredDailyHours * policyDaysForCompletion;
                      }

                      requiredHours = Math.max(0, (requiredHours || 0) - leaveHoursDeducted);
                      
                      const completion = getCompletionStatus(userStats.summary.totalHours, requiredHours);
                      
                      return (
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center">
                              {getStatusIcon(completion.status)}
                              <span className="ml-2 text-sm font-medium text-gray-900">
                                Hours Completion
                              </span>
                            </div>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(completion.status)}`}>
                              {completion.percentage.toFixed(1)}%
                            </span>
                          </div>
                          
                          <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
                            <div
                              className={`h-2 rounded-full ${
                                completion.status === 'complete' ? 'bg-green-600' :
                                completion.status === 'warning' ? 'bg-yellow-600' : 'bg-red-600'
                              }`}
                              style={{ width: `${Math.min(completion.percentage, 100)}%` }}
                            ></div>
                          </div>
                          
                          <div className="flex justify-between text-xs text-gray-500">
                            <span>{userStats.summary.totalHours}h completed</span>
                            <span>{requiredHours}h required</span>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}

                {/* Daily Breakdown */}
                {userStats.dailyHours.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Daily Breakdown</h4>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {userStats.dailyHours.map((day) => (
                        <div key={day.date} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center">
                            <div className="text-sm font-medium text-gray-900">
                              {moment(day.date).format('MMM DD, YYYY')}
                            </div>
                            <div className="ml-2 text-xs text-gray-500">
                              {moment(day.date).format('dddd')}
                            </div>
                          </div>
                          <div className="flex items-center space-x-4">
                            <div className="text-right">
                              <div className="text-sm font-medium text-gray-900">
                                {day.hoursWorked}h
                              </div>
                              {day.firstCheckIn && (
                                <div className="text-xs text-gray-500">
                                  {moment(day.firstCheckIn).format('HH:mm')} - {day.lastCheckOut ? moment(day.lastCheckOut).format('HH:mm') : 'Active'}
                                </div>
                              )}
                            </div>
                            {user?.workingPolicy && (
                              <div className="flex items-center">
                                {day.hoursWorked >= user.workingPolicy.requiredDailyHours ? (
                                  <CheckCircleIcon className="h-4 w-4 text-green-500" />
                                ) : (
                                  <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Attendance Logs */}
                {userStats.attendanceLogs.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Recent Check-ins/Check-outs</h4>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {userStats.attendanceLogs.slice(0, 10).map((log) => (
                        <div key={log.id} className="flex items-center justify-between py-1 px-2 text-sm">
                          <div className="flex items-center">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              log.punchLogType === 'CheckIn' || log.punchLogType === 'Check In'
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {log.punchLogType}
                            </span>
                            <span className="ml-2 text-gray-600">
                              {moment(log.logTime).format('MMM DD, HH:mm')}
                            </span>
                          </div>
                          <span className="text-xs text-gray-400">
                            {log.deviceNumber}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:w-auto sm:text-sm"
            >
              Close
            </button>
            <p className="mt-3 sm:mt-0 sm:mr-auto text-xs text-gray-500 flex items-center">
              <CalendarDaysIcon className="h-4 w-4 mr-1" />
              {selectedPeriod === 'custom' 
                ? `Showing data from ${moment(customStartDate).format('MMM DD, YYYY')} to ${moment(customEndDate).format('MMM DD, YYYY')}` 
                : `Showing data for ${formatPeriodLabel()}`
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UserProfileModal;
